import {
  EnrichClientTransactionsRequest,
  EnrichedCategoryReport,
  EnrichedRiskReport,
  EnrichedTransactionReport,
} from 'vopay';
import { useState } from 'react';
import { useApi } from '#shared/providers/Api';

export type EnrichedReports = {
  // Keyed by report type. Missing key: not fetched; null: no such transactions
  categories: Partial<Record<string, EnrichedCategoryReport | null>>;
  transactionReport: EnrichedTransactionReport | null;
  riskReport: EnrichedRiskReport | null;
};

export default function useFetchEnrichedReports() {
  const api = useApi();
  const [fetching, setFetching] = useState(false);
  const [reports, setReports] = useState<EnrichedReports>();

  async function fetchEnrichedReports(
    params: EnrichClientTransactionsRequest,
    { isEnhanced, reportTypes }: { isEnhanced: boolean; reportTypes: string[] },
  ) {
    try {
      setFetching(true);

      const categoryPromises = reportTypes.map(async (reportType) => {
        const res = await api.fetchClientEnrichedCategoryReport({ ...params, EnrichedDataType: reportType });
        if (!res.Success) throw new Error(res.ErrorMessage);

        return [reportType, res.EnrichedData] as const;
      });

      const [categorySettled, transactionReport, riskReport] = await Promise.all([
        // One failed category report degrades to a missing key instead of
        // rejecting the whole insights view
        Promise.allSettled(categoryPromises),
        isEnhanced
          ? api.fetchClientEnrichedTransactionReport(params).then((res) => {
              if (!res.Success) throw new Error(res.ErrorMessage);

              return res.EnrichedData;
            })
          : Promise.resolve(null),
        isEnhanced
          ? api.fetchClientEnrichedRiskReport(params).then((res) => {
              if (!res.Success) throw new Error(res.ErrorMessage);

              return res.EnrichedData;
            })
          : Promise.resolve(null),
      ]);

      const rejections = categorySettled.filter((settled) => settled.status === 'rejected');

      if (rejections.length > 0 && rejections.length === categorySettled.length) {
        throw rejections[0].reason;
      }

      rejections.forEach((rejection) => console.error(rejection.reason));

      const categories: EnrichedReports['categories'] = {};

      categorySettled.forEach((settled) => {
        if (settled.status !== 'fulfilled') return;

        const [reportType, report] = settled.value;
        categories[reportType] = report;
      });

      const result: EnrichedReports = {
        categories,
        transactionReport,
        riskReport,
      };

      setReports(result);

      return result;
    } catch (error) {
      console.error(error);
      throw error;
    } finally {
      setFetching(false);
    }
  }

  return { fetching, reports, fetchEnrichedReports };
}
